import React from "react";
import {
  Settings2,
  Plus,
  Trash2,
  Maximize2,
  Columns,
  Rows,
} from "lucide-react";

const TableEditor = ({ config, onUpdate, onDelete }) => {
  const updateCell = (r, c, updates) => {
    const newData = [...config.data.map((row) => [...row])];
    newData[r][c] = { ...newData[r][c], ...updates };
    onUpdate({ data: newData });
  };

  const addRow = () => {
    const newRow = Array(config.cols)
      .fill(null)
      .map(() => ({ content: "", subtext: "", isHeader: false }));
    onUpdate({
      rows: config.rows + 1,
      data: [...config.data, newRow],
    });
  };

  const addCol = () => {
    const newData = config.data.map((row) => [
      ...row,
      { content: "", subtext: "", isHeader: false },
    ]);
    onUpdate({
      cols: config.cols + 1,
      data: newData,
    });
  };

  const removeRow = (idx) => {
    if (config.rows <= 1) return;
    onUpdate({
      rows: config.rows - 1,
      data: config.data.filter((_, i) => i !== idx),
    });
  };

  const removeCol = (idx) => {
    if (config.cols <= 1) return;
    onUpdate({
      cols: config.cols - 1,
      data: config.data.map((row) => row.filter((_, i) => i !== idx)),
    });
  };

  const getTableClasses = () => {
    let base = "w-full text-left border-collapse ";
    if (config.stylePreset === "bordered") base += "border border-slate-200 ";
    if (config.stylePreset === "compact") base += "text-sm ";
    return base;
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
      {/* Controls Bar */}
      <div className="bg-slate-50 border-b border-slate-200 p-4 flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Rows className="w-4 h-4 text-slate-400" />
            <span className="text-xs font-bold text-slate-600">
              {config.rows} Rows
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Columns className="w-4 h-4 text-slate-400" />
            <span className="text-xs font-bold text-slate-600">
              {config.cols} Columns
            </span>
          </div>
        </div>

        <div className="h-6 w-px bg-slate-200" />

        <div className="flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.hasHeaderRow}
              onChange={(e) => onUpdate({ hasHeaderRow: e.target.checked })}
              className="rounded border-slate-300 text-primary focus:ring-indigo-500"
            />
            <span className="text-xs font-medium text-slate-600">
              Header Row
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.hasHeaderCol}
              onChange={(e) => onUpdate({ hasHeaderCol: e.target.checked })}
              className="rounded border-slate-300 text-primary focus:ring-indigo-500"
            />
            <span className="text-xs font-medium text-slate-600">
              Header Col
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.hasFooterRow}
              onChange={(e) => onUpdate({ hasFooterRow: e.target.checked })}
              className="rounded border-slate-300 text-primary focus:ring-indigo-500"
            />
            <span className="text-xs font-medium text-slate-600">
              Footer Row
            </span>
          </label>
        </div>

        <div className="h-6 w-px bg-slate-200" />

        <div className="flex items-center gap-2">
          <select
            value={config.stylePreset}
            onChange={(e) => onUpdate({ stylePreset: e.target.value })}
            className="text-xs font-bold bg-white border border-slate-200 rounded px-2 py-1 focus:outline-none"
          >
            <option value="bordered">Bordered</option>
            {/* <option value="striped">Striped</option>
                        <option value="compact">Compact</option> */}
          </select>
          <select
            value={config.mobileBehavior}
            onChange={(e) => onUpdate({ mobileBehavior: e.target.value })}
            className="text-xs font-bold bg-white border border-slate-200 rounded px-2 py-1 focus:outline-none"
          >
            <option value="scroll">Scroll</option>
            {/* <option value="stack">Stack</option> */}
          </select>
        </div>

        <div className="flex-1" />

        <button
          type="button"
          onClick={onDelete}
          className="p-1.5 text-slate-400 hover:text-red-500 transition-colors"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {/* Editor Surface */}
      <div
        className={`overflow-x-auto ${config.mobileBehavior === "stack" ? "md:overflow-x-visible" : ""}`}
      >
        <table className={getTableClasses()}>
          <thead>
            <tr className="bg-slate-50">
              <th className="w-10 p-2"></th>
              {Array.from({ length: config.cols }).map((_, c) => (
                <th key={c} className="p-2 text-center group">
                  <button
                    type="button"
                    onClick={() => removeCol(c)}
                    className="opacity-0 group-hover:opacity-100 p-1 text-slate-300 hover:text-red-500 transition-all"
                  >
                    <Trash2 size={12} />
                  </button>
                </th>
              ))}
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {config.data.map((row, r) => (
              <tr
                key={r}
                className={
                  config.stylePreset === "striped" && r % 2 !== 0
                    ? "bg-slate-50/50"
                    : ""
                }
              >
                <td className="w-10 p-2 group text-center">
                  <button
                    type="button"
                    onClick={() => removeRow(r)}
                    className="opacity-0 group-hover:opacity-100 p-1 text-slate-300 hover:text-red-500 transition-all"
                  >
                    <Trash2 size={12} />
                  </button>
                </td>
                {row.map((cell, c) => {
                  const isHeaderCell =
                    (config.hasHeaderRow && r === 0) ||
                    (config.hasHeaderCol && c === 0);
                  const isFooterCell =
                    config.hasFooterRow && r === config.data.length - 1;

                  return (
                    <td
                      key={c}
                      className={`p-3 min-w-[150px] border-b border-slate-100 align-top ${isHeaderCell ? "bg-indigo-50/30" : ""} ${isFooterCell ? "bg-slate-100/50" : ""}`}
                    >
                      <input
                        type="text"
                        placeholder={
                          isHeaderCell ? "Header Label..." : "Content..."
                        }
                        className={`w-full bg-transparent border-none p-0 focus:ring-0 text-[15px] ${isHeaderCell ? "font-bold text-slate-800" : "text-slate-600"}`}
                        value={cell.content}
                        onChange={(e) =>
                          updateCell(r, c, {
                            content: e.target.value,
                          })
                        }
                      />
                      <input
                        type="text"
                        placeholder="Subtext/Citation..."
                        className="w-full bg-transparent border-none p-0 focus:ring-0 text-[13px] text-slate-600 mt-1 placeholder:text-slate-400"
                        value={cell.subtext}
                        onChange={(e) =>
                          updateCell(r, c, {
                            subtext: e.target.value,
                          })
                        }
                      />
                      {isHeaderCell && (
                        <div className="mt-2 flex items-center gap-1">
                          <Maximize2 className="w-3 h-3 text-indigo-400" />
                          <input
                            type="text"
                            placeholder="Sort Key"
                            className="bg-white border border-indigo-100 rounded px-1.5 py-0.5 text-[12px] w-full focus:outline-none"
                            value={cell.sortKey || ""}
                            onChange={(e) =>
                              updateCell(r, c, {
                                sortKey: e.target.value,
                              })
                            }
                          />
                        </div>
                      )}
                    </td>
                  );
                })}
                <td className="w-10"></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="p-4 bg-slate-50/50 flex justify-between gap-4">
        <button
          type="button"
          onClick={addRow}
          className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-slate-500 hover:text-primary hover:bg-white rounded-lg border border-transparent hover:border-slate-200 transition-all"
        >
          <Plus size={14} /> Add Row
        </button>
        <button
          type="button"
          onClick={addCol}
          className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-slate-500 hover:text-primary hover:bg-white rounded-lg border border-transparent hover:border-slate-200 transition-all"
        >
          <Plus size={14} /> Add Column
        </button>
      </div>
    </div>
  );
};

export default TableEditor;
